<template>
    <div
        v-click-outside="close"
        class="search-suggest"
        :class="$attrs.class"
        :style="[{ zIndex: !closed || search.length ? 1 : 0 }, $attrs.style]"
    >
        <div class="search-suggest__input-wrap">
            <div class="search-suggest__input-wrap">
                <input
                    ref="input"
                    v-model="search"
                    v-bind="inputAttrs"
                    type="text"
                    class="search-suggest__input"
                    @focus="onInputFocused"
                />
                <transition name="fade" appear>
                    <div v-if="search.length" class="search-suggest__right">
                        <button type="button" class="search-suggest__clear" @click="clear">
                            <svg
                                width="13"
                                height="12"
                                viewBox="0 0 13 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <line
                                    x1="1.22855"
                                    y1="0.396447"
                                    x2="12.4786"
                                    y2="11.6464"
                                    stroke="black"
                                />
                                <line
                                    x1="12.4786"
                                    y1="0.353553"
                                    x2="1.22855"
                                    y2="11.6036"
                                    stroke="black"
                                />
                            </svg>
                        </button>
                    </div>
                </transition>
            </div>
        </div>
        <transition name="fade-down">
            <ul v-if="!hideDropdown && options.length && !closed" class="search-suggest__dropdown">
                <li v-if="$slots.firstline" class="search-suggest__option">
                    <slot name="firstline"></slot>
                </li>
                <li
                    v-for="(option, index) in options"
                    :key="index"
                    class="search-suggest__option"
                    @click="select(option)"
                >
                    <slot :item="option" :make-option-text="makeOptionText">
                        {{ option }}
                    </slot>
                </li>
                <li v-if="$slots.lastline" class="search-suggest__option">
                    <slot name="lastline"></slot>
                </li>
            </ul>
        </transition>
    </div>
</template>

<script>
import { clickOutside } from '@speechki-frontend/shared/helpers/directives/clickOutside';

export default {
    name: 'SearchSuggest',
    directives: {
        clickOutside,
    },
    inheritAttrs: false,
    props: {
        options: {
            type: Array,
            default: () => [],
        },
        optionKey: {
            type: String,
            default: 'id',
        },
        emptyText: {
            type: String,
            default: '',
        },
        debounce: {
            type: Number,
            default: 250,
        },
        minSymbols: {
            type: Number,
            default: 3,
        },
        // Suppresses the dropdown <ul> entirely (used by consumers that
        // implement a Ctrl+F-style "type + cycle matches" flow instead of an
        // autocomplete-jump-to-result list — e.g. the Professional
        // Prooflistening top panel via TextSearch.vue). Doesn't affect
        // `options`/`selected`/select() behavior at all, only the list's
        // visibility. Default false keeps every other consumer unchanged.
        hideDropdown: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'selected', 'belowMinSymbols'],
    data() {
        return {
            search: '',
            timeout: null,
            closed: false,
        };
    },
    computed: {
        inputAttrs() {
            // class/style stay on the wrapper (Vue 2 merged them there)
            const { class: _class, style: _style, ...attrs } = this.$attrs;
            return attrs;
        },
        isOpened() {
            return (this.options.length || this.search) && !this.closed;
        },
    },
    watch: {
        search() {
            if (!this.search) {
                return this.$emit('update:modelValue', this.search);
            }

            if (this.search.length < this.minSymbols) {
                // Drop a pending debounced emit for a longer query and let the
                // consumer clear results that no longer match the input.
                clearTimeout(this.timeout);
                this.$emit('belowMinSymbols', this.search);
                return;
            }

            clearTimeout(this.timeout);

            this.timeout = setTimeout(() => {
                this.$emit('update:modelValue', this.search);
                this.$refs.input.focus();
            }, this.debounce);
        },
    },
    beforeUnmount() {
        clearTimeout(this.timeout);
    },
    methods: {
        emit() {
            this.$emit('update:modelValue', this.search);
        },
        select(option) {
            this.$emit('selected', { ...option, searchString: this.search });
            this.search = '';
        },
        close() {
            this.closed = true;
        },
        onInputFocused() {
            this.closed = false;
        },
        clear() {
            this.search = '';
        },
        makeOptionText(text) {
            let index = text.toLowerCase().indexOf(this.search.toLowerCase());

            if (text.length < 74 || this.search.length > 74) {
                return (
                    text.substring(0, index) +
                    `<b>${text.substring(index, index + this.search.length)}</b>` +
                    text.substring(index + this.search.length)
                );
            } else {
                let start = Math.floor(index - (74 - this.search.length) / 2);
                if (start < 0) {
                    start = 0;
                }
                let end = Math.floor(index + this.search.length + (74 - this.search.length) / 2);
                if (start === 0) {
                    end = end + Math.floor((74 - this.search.length) / 2 - index);
                }
                if (end > text.length) {
                    end = text.length;
                }

                return (
                    `${start === 0 ? '' : '...'}` +
                    text.substring(start, index) +
                    `<b>${text.substring(index, index + this.search.length)}</b>` +
                    text.substring(index + this.search.length, end) +
                    `${end === text.length ? '' : '...'}`
                );
            }
        },
    },
};
</script>

<style lang="scss" src="./SearchSuggest.scss"></style>
